// TRAQ Manager palettes — same keys, designed separately for light and dark.
// Hex values stay real hex so `${APP_COLOURS.BLUE}20` alpha suffixes keep working.

export const STORAGE_KEY = 'traq.theme.mode';

const SHARED = {
	PRIMARY: '',
	SECONDARY: '#748969',
	TEXTINPUT: '#0720D5',
	DESELECTEDCOLOR: '#EEF2FC',
	WHITE: '#FFF',
	GREEN: '#07D55D',
	ORANGE: '#FFD76E',
	RED: '#FF5F5F',
	BLUE: '#0068FF',
	BLUE2: '#00FFF9',
	BLACK: '#0f1318',
	CRYPTO_GREEN: '#00FF88',
	CRYPTO_RED: '#FF3B3B',
	CRYPTO_YELLOW: '#FFD700',
	CRYPTO_PURPLE: '#9945FF',
	CRYPTO_CYAN: '#00E0FF',
	CHART_UP: '#00C853',
	CHART_DOWN: '#FF1744',
	GRADIENT_START: '#1A237E',
	GRADIENT_END: '#311B92',
	SUCCESS_GREEN: '#00E676',
	WARNING_AMBER: '#FFC400',
	ERROR_RED: '#FF1744',
	SUCCESS: '#00E676',
	WARNING: '#FFC400',
	ERROR: '#FF1744',
	PURPLE: '#9945FF',
};

export const DARK_COLOURS = {
	...SHARED,
	BACKGROUND: '#12161c',
	TEXTCOLOR: '#F3F0F8',
	PLACEHOLDER: '#8B8498',
	OFFWHITE: '#FBFBFC',
	TRANSHALO: 'rgba(255, 255, 255, 0.01)',
	BG: '#12161c',
	BG2: '#12161c',
	BG3: '#161a22',
	BG4: '#1a1f28',
	BG5: '#242830',
	BG6: '#3F4659',
	CARD: '#1a1f28',
	CARD_HOVER: '#1f2538',
	WHITE_TRANSPARENT: 'rgba(255, 255, 255, 0.02)',
	BLACKTRANS: 'rgba(255, 255, 255, 0.01)',
	CARD_BG: 'rgba(255, 255, 255, 0.05)',
	CARD_BORDER: 'rgba(255, 255, 255, 0.1)',
	HOVER_BG: 'rgba(255, 255, 255, 0.07)',
	ACTIVE_BG: 'rgba(255, 255, 255, 0.12)',
	OVERLAY: 'rgba(8, 10, 14, 0.72)',
	SHADOW: 'rgba(0, 0, 0, 0.45)',
};

export const LIGHT_COLOURS = {
	...SHARED,
	BACKGROUND: '#F4F2F8',
	TEXTCOLOR: '#16131E',
	PLACEHOLDER: '#6B6478',
	OFFWHITE: '#1C1826',
	TRANSHALO: 'rgba(28, 20, 48, 0.02)',
	BG: '#F4F2F8',
	BG2: '#FBFAFC',
	BG3: '#FFFFFF',
	BG4: '#EEEAF4',
	BG5: '#E4DEEE',
	BG6: '#C9C0D6',
	CARD: '#FFFFFF',
	CARD_HOVER: '#F6F4FA',
	WHITE_TRANSPARENT: 'rgba(28, 20, 48, 0.03)',
	BLACKTRANS: 'rgba(28, 20, 48, 0.02)',
	CARD_BG: 'rgba(28, 20, 48, 0.04)',
	CARD_BORDER: 'rgba(28, 20, 48, 0.10)',
	HOVER_BG: 'rgba(28, 20, 48, 0.05)',
	ACTIVE_BG: 'rgba(28, 20, 48, 0.09)',
	OVERLAY: 'rgba(22, 19, 30, 0.44)',
	SHADOW: 'rgba(22, 19, 30, 0.12)',
};

export const PALETTES = {
	dark: DARK_COLOURS,
	light: LIGHT_COLOURS,
};

export const APP_COLOURS = { ...DARK_COLOURS };

const CSS_VARS = {
	'--bg-primary': 'BG',
	'--bg-secondary': 'BG2',
	'--bg-tertiary': 'BG3',
	'--bg-dark': 'BG5',
	'--bg-darker': 'BG',
	'--text-primary': 'TEXTCOLOR',
	'--text-secondary': 'PLACEHOLDER',
	'--text-tertiary': 'PLACEHOLDER',
	'--text-inverse': 'WHITE',
	'--border-light': 'BG5',
	'--border-medium': 'BG6',
	'--card': 'CARD',
	'--overlay': 'OVERLAY',
};

export function resolveScheme(mode) {
	if (mode === 'light' || mode === 'dark') return mode;
	if (typeof window === 'undefined') return 'dark';
	return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function readStoredMode() {
	try {
		const saved = localStorage.getItem(STORAGE_KEY);
		if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
	} catch {
		// localStorage blocked — fall through
	}
	return 'dark';
}

export function applyScheme(scheme) {
	const next = PALETTES[scheme] || DARK_COLOURS;
	Object.assign(APP_COLOURS, next);

	if (typeof document === 'undefined') return next;

	const root = document.documentElement;
	root.setAttribute('data-theme', scheme);
	root.style.colorScheme = scheme;

	Object.entries(CSS_VARS).forEach(([cssVar, key]) => {
		root.style.setProperty(cssVar, next[key]);
	});

	const meta = document.querySelector('meta[name="theme-color"]');
	if (meta) meta.setAttribute('content', next.BG);

	return next;
}

// Apply before first React paint so inline styles read the right palette.
if (typeof window !== 'undefined') {
	applyScheme(resolveScheme(readStoredMode()));
}

export default APP_COLOURS;
